// #3564 decision 2 (AVR, PR #3666 comment 6327023198) — secrets in URLs never
// reach Sentry.
//
// A secret in the URL itself is out of reach of the key rule: it rides in
// request.url, request.query_string, the transaction name, breadcrumbs
// (data.url / from / to) and span data (http.url, url.full, url.query,
// http.target, …), in the API and in the browser. This is the ONE helper both
// scrubbers apply (apps/api/src/lib/sentryScrub.ts, apps/web/src/lib/sentryConfig.ts):
// they run it on every string in an event, so a URL is masked wherever it is
// repeated, and on each pair of a structured query_string.
//
// It masks:
//   1. the token segment of each known secret route's path (SECRET_PATH_RULES,
//      from the route sweep listed in PR #3666), e.g. /invite/[Filtered]/validate;
//   2. the value of any query (or fragment) parameter whose key matches
//      SENSITIVE_KEY — no gen_ai.usage. exemption here, that is the key rule's
//      alone — or is exactly `code` (the OAuth one-time codes the sweep found,
//      AVR 6334002259) or `state` (AVR 6334009565); URL-only, never the key rule;
//   3. a parameter whose value is itself a URL carrying one of the above
//      (?redirect=/accept-invite%3Ftoken%3D…), filtered whole. Nested deeper
//      than MAX_NESTED, a value that still holds a path or a parameter is
//      filtered whole without being read (fail closed).
//   4. (round 3, AVR 6333994064) everything after the host — path, query and
//      fragment — of an outbound webhook URL (WEBHOOK_HOST_RULES), and the
//      Azure SAS parameters `sig`, `sv` and `se` on any host. The webhook
//      hosts are the ones the code itself accepts; see WEBHOOK_HOST_RULES.
//      An outbound span also carries its path and query in host-less
//      attributes (url.path, url.query, …); webhookSiblingKeys names those so
//      the API scrubber masks them when the same span names a webhook host.
// Pure and synchronous; never throws, on any input: a non-string is returned
// as it came, and a string it fails on comes back as "[Filtered]".

export const FILTERED = '[Filtered]';

/** AVR's key pattern (#3564, 6302450253): the key rule and the URL query rule share it. */
export const SENSITIVE_KEY = /pass(word)?|secret|token|api[-_]?key|credential|auth/i;

// URL query keys masked beyond AVR's key pattern, in URLs only (never by the
// body/key rule):
//   - `code` (6334002259): the OAuth one-time codes — the provider's ?code= on
//     every callback, our own /auth/callback?code= (exchanged for a session
//     JWT) and /verify-email?code=;
//   - `state` (6334009565): on the Drive and BYOI callbacks it is the
//     capability that completes the connection; on every route.
const OAUTH_URL_KEY = /^(?:code|state)$/i;
// Azure SAS-style parameters (6333994064), on any host: Logic Apps triggers
// carry their secret in ?sig=.
const SAS_URL_KEY = /^(?:sig|sv|se)$/i;

// Outbound webhook hosts, taken from the code's own validation — the only
// hosts a tenant's webhook URL can be stored with:
//   - hooks.slack.com: apps/api/src/routes/slack-channel.ts:61
//     (`u.hostname === 'hooks.slack.com'`);
//   - <label>.webhook.office.com and <label>.logic.azure.com:
//     apps/api/src/routes/teams-channel.ts:58-59 (TEAMS_ALLOWED_HOSTS,
//     `^[a-z0-9-]+\.webhook\.office\.com$`, `^[a-z0-9-]+\.logic\.azure\.com$`).
// One label only, as the validator accepts. Posted from
// services/slack.service.ts:64 and services/teams.service.ts:63.
const WEBHOOK_HOSTS = [
  String.raw`hooks\.slack\.com`,
  String.raw`[a-z0-9-]+\.webhook\.office\.com`,
  String.raw`[a-z0-9-]+\.logic\.azure\.com`,
] as const;
/**
 * Each webhook host's URL: group 1 (`//`, userinfo, host, port) is kept, and
 * so is the scheme before it; everything after it becomes "/[Filtered]". The
 * match starts at `//`, not at the scheme: an optional scheme prefix is tried
 * at every letter of a long word, which is quadratic.
 */
export const WEBHOOK_HOST_RULES: readonly RegExp[] = WEBHOOK_HOSTS.map(
  (host) => new RegExp(String.raw`(//(?:[^/?#@\s"'<>]*@)?${host}(?::\d*)?)[/?#][^\s"'<>]*`, 'gi'),
);
// The same rules without `g`, compiled once, for test(): a global regex keeps
// lastIndex between test() calls, so reusing WEBHOOK_HOST_RULES there would
// give alternating answers on the same input.
const WEBHOOK_URL_TESTS: readonly RegExp[] = WEBHOOK_HOST_RULES.map((re) => new RegExp(re.source, 'i'));
const WEBHOOK_HOSTNAME = WEBHOOK_HOSTS.map((host) => new RegExp(String.raw`^${host}(?::\d*)?$`, 'i'));

// Where a URL token stops: the next path, query or fragment delimiter, or
// anything that can't sit inside a URL in a string.
const SEG = String.raw`[^/?#&\s"'<>]+`;
const END = String.raw`(?=[/?#&\s"'<>]|$)`;
// Where a route WORD stops (redeem, status, accept, validate, a placeholder's
// name): anything that can't continue the word, so `…/status: 404` and
// `…/validate).` in free text still match (R2 F1, bfcb5422).
const WORD_END = String.raw`(?![A-Za-z0-9_-])`;
// A route template's own placeholder (GET /invitations/:token/status) is not a token.
const NOT_PLACEHOLDER = String.raw`(?!:[A-Za-z_]\w*${WORD_END})`;

/** Each known secret route: group 1 is kept, the token segment after it becomes "[Filtered]". */
export const SECRET_PATH_RULES: readonly RegExp[] = [
  // GET /action/:token — the legacy magic link (routes/magic-links.ts). /action/redeem is not a token.
  new RegExp(String.raw`(/action/)(?!redeem${WORD_END})${NOT_PLACEHOLDER}${SEG}${END}`, 'gi'),
  // GET /invitations/:token/status, POST /invitations/:token/accept (routes/invitations.ts).
  // Not /projects/:projectId/invitations/:invitationId/accept: that id is a row id, not a secret.
  new RegExp(String.raw`(?<!/projects/[^/?#&\s"'<>]+)(/invitations/)${NOT_PLACEHOLDER}${SEG}(?=/(?:status|accept)${WORD_END})`, 'gi'),
  // GET /partner/clients/invite/:token/validate (routes/partner-client-invite.router.ts)
  new RegExp(String.raw`(/invite/)${NOT_PLACEHOLDER}${SEG}(?=/validate${WORD_END})`, 'gi'),
];

// key=value after a query or fragment delimiter, or at the start of a bare query string.
const QUERY_PARAM = /(^|[?&#;])([^=&#?;\s"'<>]+)=([^&#;\s"'<>]*)/g;

const MAX_NESTED = 3;

/** One round of percent-decoding that never throws (a malformed escape falls back to each well-formed %XX). */
function decodeOnce(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s.replace(/%([0-9a-f]{2})/gi, (_m, hex: string) => String.fromCharCode(parseInt(hex, 16)));
  }
}

function isSecretQueryKey(rawKey: string): boolean {
  // Decoded until stable (bounded), so a double-encoded key (to%256Ben) is judged too (R2 F4).
  let key = rawKey;
  for (let i = 0; i < 5; i++) {
    const next = decodeOnce(key);
    if (next === key) break;
    key = next;
  }
  key = key.trim();
  return SENSITIVE_KEY.test(key) || OAUTH_URL_KEY.test(key) || SAS_URL_KEY.test(key);
}

function mask(s: string, depth: number): string {
  let out = s;
  for (const rule of WEBHOOK_HOST_RULES) out = out.replace(rule, `$1/${FILTERED}`);
  for (const rule of SECRET_PATH_RULES) out = out.replace(rule, `$1${FILTERED}`);
  return out.replace(QUERY_PARAM, (whole, sep: string, key: string, value: string) => {
    if (isSecretQueryKey(key)) return `${sep}${key}=${FILTERED}`;
    const inner = decodeOnce(value);
    if (depth >= MAX_NESTED) return /[/=]/.test(inner) ? `${sep}${key}=${FILTERED}` : whole;
    if (mask(inner, depth + 1) !== inner) return `${sep}${key}=${FILTERED}`;
    return whole;
  });
}

/** Masks URL secrets in a string (a URL, a path, a bare query string, a transaction name, any text). */
export function maskUrlSecrets<T>(value: T): T {
  if (typeof value !== 'string') return value;
  try {
    return mask(value, 0) as unknown as T;
  } catch {
    return FILTERED as unknown as T;
  }
}

// The host-less attributes in which an outbound span or breadcrumb repeats its
// URL's path and query (@sentry/node 10.46 node-fetch: url.path, url.query,
// http.query; the http integration: http.target, http.fragment).
const PATH_QUERY_KEYS = ['url.path', 'url.query', 'url.fragment', 'http.target', 'http.query', 'http.fragment'] as const;
// Where that object names its host on its own.
const HOST_KEYS = ['server.address', 'net.peer.name', 'net.host.name', 'http.host'] as const;

function namesWebhookHost(obj: Record<string, unknown>): boolean {
  for (const k of HOST_KEYS) {
    const v = obj[k];
    if (typeof v === 'string' && WEBHOOK_HOSTNAME.some((re) => re.test(v.trim()))) return true;
  }
  for (const v of Object.values(obj)) {
    if (typeof v === 'string' && WEBHOOK_URL_TESTS.some((re) => re.test(v))) return true;
  }
  return false;
}

/**
 * The keys of `obj` whose string values must become "[Filtered]" because the
 * same object (a span's data, a breadcrumb's data) names a webhook host: its
 * path and query attributes carry the secret without the host, so the string
 * rule alone can't see it. [] when the object names no webhook host, is not a
 * plain object, or can't be read. Never throws.
 */
export function webhookSiblingKeys(obj: unknown): string[] {
  try {
    if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return [];
    const rec = obj as Record<string, unknown>;
    if (!namesWebhookHost(rec)) return [];
    return PATH_QUERY_KEYS.filter((k) => typeof rec[k] === 'string' && rec[k] !== FILTERED);
  } catch {
    return [];
  }
}

/**
 * request.query_string in any shape the SDK may send: a string, [key, value]
 * pairs, or a { key: value } map. Each pair goes through maskUrlSecrets as
 * `key=value`, so the same rules decide. Returns the value itself when nothing
 * changes; anything it can't read comes back "[Filtered]".
 */
export function maskQueryString(qs: unknown): unknown {
  try {
    if (qs === null || qs === undefined) return qs;
    if (typeof qs === 'string') return maskUrlSecrets(qs);
    if (typeof qs !== 'object') return qs;
    const pairChanged = (k: unknown, v: unknown) => {
      if (typeof k !== 'string') return false;
      // A non-string value (a repeated key's array, a number) is judged by its key alone.
      const sample = typeof v === 'string' ? v : 'x';
      return maskUrlSecrets(`${k}=${sample}`) !== `${k}=${sample}`;
    };
    if (Array.isArray(qs)) {
      let changed = false;
      const next = qs.map((pair) => {
        if (!Array.isArray(pair) || !pairChanged(pair[0], pair[1])) return pair;
        changed = true;
        return [pair[0], FILTERED];
      });
      return changed ? next : qs;
    }
    let changed = false;
    const next: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(qs as Record<string, unknown>)) {
      if (pairChanged(k, v)) { next[k] = FILTERED; changed = true; } else next[k] = v;
    }
    return changed ? next : qs;
  } catch {
    return FILTERED;
  }
}
